import { Button } from 'blaze-ui/Button'
import Styles from './MobilePills.module.scss'
import { Edit1, Plus } from 'blaze-ui/icons'
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react'
import { EditorPill, MobilePillEditor } from 'blaze-ui/MobilePillEditor/MobilePillEditor'
import { nanoid } from 'nanoid'
import { useRootStore } from 'stores/RootStore'
import { Pill } from 'components/Pill/Pill'

type Props = {
  title: string
  values: string[]
  onUpdate: (values: string[]) => void
  prefix?: string
}

export const MobilePills = forwardRef<PillsRef, Props>((props, ref) => {
  const { title, values, onUpdate, prefix = '' } = props

  const { drawersStore } = useRootStore()
  const { closeDrawer, openDrawer } = drawersStore

  const pills = useMemo<EditorPill[]>(() => values.map((value) => ({ id: nanoid(), label: value })), [values])

  const handleUpdate = useCallback(
    (newPills: EditorPill[]) => {
      onUpdate(newPills.map((pill) => pill.label.trim()))
    },
    [onUpdate],
  )

  const openEditor = useCallback(
    (initiallyEditing = true) => {
      openDrawer(MobilePillEditor, {
        open: true,
        pills,
        title,
        onClose: closeDrawer,
        onUpdate: handleUpdate,
        initiallyEditing,
        prefix,
      })
    },
    [pills, title, openDrawer, closeDrawer, handleUpdate, prefix],
  )

  useImperativeHandle(
    ref,
    () => ({
      edit: () => openEditor(false),
    }),
    [openEditor],
  )

  return (
    <div className={Styles.root}>
      {pills.length > 0 ? (
        <>
          {pills.map((pill) => (
            <div key={pill.id} onClick={() => openEditor(false)} className={Styles.pillContainer}>
              <Pill size="xl" className={Styles.pill}>
                {pill.label}
              </Pill>
            </div>
          ))}
        </>
      ) : (
        <AddPill onPress={openEditor} />
      )}
    </div>
  )
})

const AddPill = ({ onPress }: { onPress: () => void }) => {
  return (
    <Button
      className={Styles.addPill}
      variant="ghost-tertiary"
      frontIcon={Plus}
      size="md"
      onPress={onPress}
      aria-label={'Add'}
    >
      Add
    </Button>
  )
}

type Output = {
  Pills: () => JSX.Element
  EditPillsButton: () => JSX.Element
}

type PillsRef = {
  edit: () => void
}

export const useMobilePills = (props: Props): Output => {
  const { title, values, onUpdate, prefix = '' } = props
  const pillsRef = useRef<PillsRef>(null)

  const Pills = useCallback(() => {
    return <MobilePills title={title} values={values} onUpdate={onUpdate} ref={pillsRef} prefix={prefix} />
  }, [title, values, onUpdate, prefix])

  const handleEdit = useCallback(() => {
    pillsRef.current?.edit()
  }, [])

  const EditPillsButton = useCallback(() => {
    return (
      <Button
        className={values.length ? undefined : Styles.hidden}
        variant="ghost"
        size="sm"
        frontIcon={Edit1}
        aria-label={`Edit ${title}`}
        onPress={handleEdit}
      />
    )
  }, [handleEdit, title, values.length])

  return { Pills, EditPillsButton }
}
